import { Component, inject, signal, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { UiOrchestratorService } from '../../../../core/services/ui-orchestrator.service';
import { ApiService } from '../../../../core/services/api.service';
import { AuthService } from '../../../../core/services/auth.service';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { environment } from '../../../../../environments/environment';

interface ClockInPayload {
  employeeId: string;
  checkInTime: string;
  method: string;
  location: string;
  deviceId: string;
}

interface ClockOutPayload {
  employeeId: string;
  checkOutTime: string;
  method: string;
  location: string;
  deviceId: string;
}

interface ClockInResponse {
  message?: string;
  data?: { sessionId?: string; eventId?: string };
}

interface ClockOutResponse {
  message?: string;
  data?: { hoursWorked?: number; totalHours?: number };
}

@Component({
  selector: 'app-punch-clock',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule],
  templateUrl: './punch-clock.component.html'
})
export class PunchClockComponent implements OnInit, OnDestroy {
  private api        = inject(ApiService);
  private ui         = inject(UiOrchestratorService);
  private auth       = inject(AuthService);
  private platformId = inject(PLATFORM_ID);
  private readonly base = (environment.attendanceBaseUrl ?? '').replace(/\/+$/, '');

  isClockedIn  = signal(false);
  isLoading    = signal(false);
  clockInTime  = signal<Date | null>(null);
  elapsed      = signal('00:00:00');
  sessionId    = signal<string | null>(null);

  private readonly deviceId   = 'web-' + (typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 20).replace(/\s/g, '-') : 'ssr');
  private timerRef: ReturnType<typeof setInterval> | undefined;

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const saved = localStorage.getItem('punch_session');
    if (saved) {
      try {
        const s = JSON.parse(saved);
        this.isClockedIn.set(true);
        this.clockInTime.set(new Date(s.clockInTime));
        this.sessionId.set(s.sessionId);
        this.startTimer();
      } catch { localStorage.removeItem('punch_session'); }
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.timerRef);
  }

  private startTimer(): void {
    clearInterval(this.timerRef);
    this.timerRef = setInterval(() => {
      const start = this.clockInTime();
      if (!start) return;
      const diff = Math.floor((Date.now() - start.getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      this.elapsed.set(
        `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    }, 1000);
  }

  /** Local date key (YYYY-MM-DD) for scoping today's stored punch. */
  private todayKey(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private getLocation(): Promise<string> {
    return new Promise(resolve => {
      if (typeof navigator === 'undefined' || !navigator.geolocation) { resolve('Web App'); return; }
      navigator.geolocation.getCurrentPosition(
        pos => resolve(`Lat: ${pos.coords.latitude.toFixed(4)}, Lon: ${pos.coords.longitude.toFixed(4)}`),
        ()  => resolve('Web App'),
        { timeout: 4000 }
      );
    });
  }

  async clockIn(): Promise<void> {
    const empId = this.auth.loggedEmployeeId();
    if (!empId) {
      this.ui.toast({ severity: 'error', summary: 'Clock-In Failed', detail: 'Employee ID is missing. Please log in again.' });
      return;
    }
    this.isLoading.set(true);
    const location = await this.getLocation();
    const payload: ClockInPayload = {
      employeeId:  empId,
      checkInTime: new Date().toISOString(),
      method:      'WEB',
      location,
      deviceId:    this.deviceId
    };

    this.api.post<ClockInResponse>(`${this.base}/attendance/clock-in`, payload).subscribe({
      next: res => {
        const now = new Date();
        this.isClockedIn.set(true);
        this.clockInTime.set(now);
        this.sessionId.set(res?.data?.sessionId ?? res?.data?.eventId ?? null);
        localStorage.setItem('punch_session', JSON.stringify({
          clockInTime: now.toISOString(),
          sessionId:   this.sessionId()
        }));
        // Today's in/out, read by the dashboard clock card (survives clock-out).
        localStorage.setItem('punch_today', JSON.stringify({
          date:     this.todayKey(),
          clockIn:  now.toISOString(),
          clockOut: null
        }));
        this.startTimer();
        this.isLoading.set(false);
        this.ui.toast({ severity: 'success', summary: 'Clocked In', detail: res?.message ?? 'Clock-in recorded.' });
      },
      error: (err: { error?: { message?: string; error?: string } }) => {
        this.isLoading.set(false);
        const msg = err?.error?.message ?? err?.error?.error ?? 'Clock-in failed.';
        this.ui.toast({ severity: 'error', summary: 'Clock-In Failed', detail: msg });
      }
    });
  }

  async clockOut(): Promise<void> {
    const empId = this.auth.loggedEmployeeId();
    if (!empId) {
      this.ui.toast({ severity: 'error', summary: 'Clock-Out Failed', detail: 'Employee ID is missing. Please log in again.' });
      return;
    }
    this.isLoading.set(true);
    const location = await this.getLocation();
    const payload: ClockOutPayload = {
      employeeId:   empId,
      checkOutTime: new Date().toISOString(),
      method:       'WEB',
      location,
      deviceId:     this.deviceId
    };

    this.api.post<ClockOutResponse>(`${this.base}/attendance/clock-out`, payload).subscribe({
      next: res => {
        clearInterval(this.timerRef);
        this.isClockedIn.set(false);
        this.clockInTime.set(null);
        this.sessionId.set(null);
        this.elapsed.set('00:00:00');
        localStorage.removeItem('punch_session');
        // Stamp today's clock-out so the dashboard card can show both times.
        try {
          const t = JSON.parse(localStorage.getItem('punch_today') ?? '{}');
          localStorage.setItem('punch_today', JSON.stringify({
            date:     this.todayKey(),
            clockIn:  t?.clockIn ?? null,
            clockOut: new Date().toISOString()
          }));
        } catch { /* ignore malformed */ }
        this.isLoading.set(false);
        const hours = res?.data?.hoursWorked ?? res?.data?.totalHours ?? '';
        const detail = hours
          ? `${res?.message ?? 'Clock-out recorded.'} · ${hours}h worked`
          : (res?.message ?? 'Clock-out recorded.');
        this.ui.toast({ severity: 'success', summary: 'Clocked Out', detail });
      },
      error: (err: { error?: { message?: string; error?: string } }) => {
        this.isLoading.set(false);
        const msg = err?.error?.message ?? err?.error?.error ?? 'Clock-out failed.';
        this.ui.toast({ severity: 'error', summary: 'Clock-Out Failed', detail: msg });
      }
    });
  }
}
